/* eslint-disable no-shadow */
import _get from 'lodash/get';
import _noop from 'lodash/noop';
import _pick from 'lodash/pick';
import React, { useEffect, useCallback } from 'react';
import { connect } from 'react-redux';

import {
  getGradebook,
  getGradebookInstructor,
  getIndividualStudentData,
  overrideGrade,
  setGradebookV2Active,
  updateAlert,
  updateAssignmentAverages,
  updateGradebook,
} from '../../../actions/gradebook';
import { getConfigObject } from '../../../utils/config';
import {
  handleAlertReef,
  isUserLinked,
  unlinkReefInstructor,
} from '../../../actions/reef';
import { APP_LOADING } from '../../../redux/constants';
import getEmptyGradebookGridData from '../../../selectors/empty-gradebook';
import getGradebookGridData from '../../../selectors/gradebook';
import { enrolledLengthSelector } from '../../../selectors/instructor-gradebook';
import { useFullRoster } from '../../../hooks/useFullRoster';
import {
  searchDateFromSelector,
  searchDateToSelector,
} from '../../../selectors/search-gradebook';
import { selectShowPercent } from '../../../selectors/settings';
import ExportDialog from '../../modals/ExportDialog';
import GradebookSettings from '../../modals/GradebookSettings/GradebookSettings';
import IndividualStudentGradebook from '../../modals/IndividualStudentGradebook/IndividualStudentGradebook';
import { LinkReefDialog } from '../../modals/LinkReefDialog/LinkReefDialog';
import OverrideGradeDialog from '../../modals/OverrideGradeDialog';
import UnlinkReefDialog from '../../modals/UnlinkReefDialog';
import useModalStates from '../../modals/useModalStates';
import InstructorGradebook from '../InstructorGradebook/InstructorGradebook';

function InstructorGradebookContainer({
  courseId,
  lmsId,
  isSectionManager,
  isGatewayEnabled,
  isRestrictedSection,
  courseShortName = '',
  getGradebook,
  getGradebookInstructor,
  loading,
  user,
  instructor,
  permissions,
  rdxGradebookSettings,
  collapsedCategoryIds,
  isUserLinked,
  getLaunchUrl = _noop,
  handleLocalLaunch = _noop,
  showPercent,
  exportMenuOpen,
  reefMenuOpen,
  isGradebookFetching,
  isGradebookReFetching,
  isSettingsModalMounted,
  showGradeOverrideDialog,
  gradeOverrideData,
  hasInstructorLinkedCourse,
  showFilterOptions,
  onTabChange,
  hasStudentReport,
  getStudentReportLink,
  handleCloseReefMenu,
  handleReefMenuClick,
  alert,
  handleAlertReef,
  anchorEl,
  pageCount,
  showSettings,
  dispatch,
  page,
  limit,
  count,
  searchPhrase,
  searchType,
  exceptionGroups,
  filterTypes,
  searchDateFrom,
  searchDateTo,
  gradebookGridData,
  updateAssignmentAverages,
  getIndividualStudentData,
  hasInstructorRestrictedCourseLevelRole,
  showIndividualStudentGradebook,
  disableEnforceFocus,
  sortColumn,
  sortDirection,
  averages,
  isGradebookV2Active,
  setGradebookV2Active,
}) {
  const { FF_IS_INSTRUCTOR_GRADEBOOK_V2_ENABLED = false } = getConfigObject();

  const {
    showLinkReefDialog,
    linkReefDialog,
    showUnlinkReefDialog,
    unlinkReefDialog,
    exportDialog,
  } = useModalStates();

  const fullRoster = useFullRoster(courseId);
  const enrolledLength = enrolledLengthSelector(fullRoster);

  // Initialize V2 state from feature flag on first render
  useEffect(() => {
    setGradebookV2Active(FF_IS_INSTRUCTOR_GRADEBOOK_V2_ENABLED);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handler for toggling between V1 and V2 - re-fetches data with appropriate endpoint
  const handleToggleGradebookV2 = useCallback(() => {
    const newIsV2 = !isGradebookV2Active;
    setGradebookV2Active(newIsV2);

    // Re-fetch data with appropriate endpoint
    const options = { page: 1, forceInitialFetchLoadingIndicator: true };
    if (newIsV2) {
      getGradebookInstructor(courseId, options);
    } else {
      getGradebook(courseId, options);
    }
  }, [
    isGradebookV2Active,
    courseId,
    getGradebook,
    getGradebookInstructor,
    setGradebookV2Active,
  ]);

  useEffect(() => {
    // Use feature flag directly to avoid race condition on initial mount.
    // On first render, isGradebookV2Active is false (Redux initial state) while
    // the first useEffect dispatches the action to set it true. By checking the
    // feature flag directly, we ensure the correct endpoint is called immediately.
    // After mount, isGradebookV2Active will be synced to Redux and subsequent
    // fetches (from toggle, etc.) will use the Redux state correctly.
    const shouldUseV2 =
      isGradebookV2Active || FF_IS_INSTRUCTOR_GRADEBOOK_V2_ENABLED;

    if (shouldUseV2) {
      getGradebookInstructor(courseId, {
        forceInitialFetchLoadingIndicator: true,
      });
    } else {
      getGradebook(courseId, {
        forceInitialFetchLoadingIndicator: true,
      });
    }
    isUserLinked(courseId);
  }, [
    courseId,
    getGradebook,
    getGradebookInstructor,
    isUserLinked,
    isGradebookV2Active,
    FF_IS_INSTRUCTOR_GRADEBOOK_V2_ENABLED,
  ]);

  useEffect(() => {
    // If we have an iClicker mapping (from hasInstructorLinkedCourse)
    // but the user is not authenticated with iClicker (from isReefLinked)
    // then we have a mismatch and should show an alert.
    // Wait until isReefStatusFetching is false to avoid a race conditions
    if (
      hasInstructorLinkedCourse &&
      !user.isReefLinked &&
      !user.isReefStatusFetching
    ) {
      dispatch(
        updateAlert({
          show: true,
          type: 'warning',
          dismissible: true,
          text: `There is an issue with your Achieve and iClicker connection. Please use the iClicker button to reconnect the course (and then unlink if desired)`,
        }),
      );
    }
  }, [
    hasInstructorLinkedCourse,
    user.isReefLinked,
    user.isReefStatusFetching,
    dispatch,
  ]);

  const getNextGradebookPage = (page) => {
    if (isGradebookV2Active) {
      dispatch(getGradebookInstructor(courseId, { page, isNextPage: true }));
    } else {
      dispatch(getGradebook(courseId, { page, isNextPage: true }));
    }
  };

  const handleShowSettings = () => {
    dispatch(
      updateGradebook({
        showSettings: true,
        isSettingsModalMounted: true,
      }),
    );
  };

  const hideSettings = () => {
    dispatch(
      updateGradebook({
        showSettings: false,
      }),
    );
  };

  const renderSettingsModal = () => {
    // handleModalExited is an unfortunate hack to clean up the state
    // of the settings modal once it's completely transitioned off the screen
    // This is needed because the modal copies props into the components state
    // and every time the modal is shown, it needs a fresh copy of data
    //
    // showSettings - determines if the modal should transition up or down onto the screen
    // isSettingsModalMounted - determines if the modal is mounted in the first place
    //
    return (
      <GradebookSettings
        user={user}
        courseId={courseId}
        isOpen={showSettings}
        isSectionManager={isSectionManager}
        isRestrictedSection={isRestrictedSection}
        isDisabled={
          !isSectionManager &&
          (isRestrictedSection || hasInstructorRestrictedCourseLevelRole)
        }
        hasInstructorLinkedCourse={hasInstructorLinkedCourse}
        handleSettingsClose={() => hideSettings()}
        handleModalExited={() =>
          dispatch(updateGradebook({ isSettingsModalMounted: false }))
        }
        disableEnforceFocus={disableEnforceFocus}
      />
    );
  };

  const handleGradeOverride = (info) => {
    const obj = {
      ...info,
      courseId: courseId,
      isGradebookV2Active,
    };

    const options = {
      limit: limit,
      page: page,
    };

    dispatch(overrideGrade(obj, options))
      .then((data) => {
        if (data) {
          dispatch(
            updateAlert({
              show: true,
              type: 'success',
              dismissible: true,
              text: data.success,
            }),
          );
          if (showIndividualStudentGradebook) {
            getIndividualStudentData(courseId, obj.studentId, false);
          }
        }
      })
      .catch(() => {
        dispatch({
          type: APP_LOADING,
          payload: false,
        });
        dispatch(
          updateAlert({
            show: true,
            type: 'warning',
            dismissible: true,
            text: 'Grade update failed',
          }),
        );
      })
      .finally(() => handleCloseGradeOverrideDialog());
  };

  const refreshGradebookOnSidePanelUpdate = ({ assignmentId, studentId }) => {
    const options = {
      limit: limit,
      page: page,
    };

    if (isGradebookV2Active) {
      getGradebookInstructor(courseId, options);
    } else {
      getGradebook(courseId, options);
    }
    updateAssignmentAverages([assignmentId]);

    if (showIndividualStudentGradebook && studentId && studentId !== '') {
      getIndividualStudentData(courseId, studentId, false);
    }
  };

  const handleShowGradeOverrideDialog = (gradeOverrideData) => {
    dispatch(
      updateGradebook({
        showGradeOverrideDialog: true,
        gradeOverrideData,
      }),
    );
    dispatch(updateAlert({ show: false }));
  };

  const handleCloseGradeOverrideDialog = () => {
    dispatch(
      updateGradebook({
        showGradeOverrideDialog: false,
        gradeOverrideData: {},
      }),
    );
  };

  handleReefMenuClick = (event) => {
    event.preventDefault();
    dispatch(
      updateGradebook({
        reefMenuOpen: true,
        anchorEl: event.currentTarget,
      }),
    );
  };

  handleCloseReefMenu = () => {
    dispatch(
      updateGradebook({
        reefMenuOpen: false,
      }),
    );
  };

  const renderGradeOverrideDialog = () => {
    return (
      <OverrideGradeDialog
        gradeOverrideData={gradeOverrideData}
        onClose={() => {
          handleCloseGradeOverrideDialog();
        }}
        onSubmit={(obj) => {
          const info = {
            ...gradeOverrideData,
            ...obj,
          };
          handleGradeOverride(info);
        }}
        show={showGradeOverrideDialog}
        dispatch={dispatch}
        courseId={courseId}
        rdxGradebookSettings={rdxGradebookSettings}
        disableEnforceFocus={disableEnforceFocus}
      />
    );
  };

  const renderLinkReefDialog = () => {
    return (
      <LinkReefDialog
        onClose={linkReefDialog.close}
        dispatch={dispatch}
        userIsInstructor
        courseId={courseId}
        isUserReefLinked={_get(user, 'isReefLinked')}
        handleAlertReef={handleAlertReef}
        hasInstructorLinkedCourse={hasInstructorLinkedCourse}
        disableEnforceFocus={disableEnforceFocus}
      />
    );
  };

  const handleUnlinkReef = () => {
    dispatch(unlinkReefInstructor(courseId))
      .then(() => {
        dispatch(
          updateAlert({
            show: true,
            type: 'success',
            dismissible: true,
            text: 'Your iClicker account is now disconnected from achieve',
          }),
        );
      })
      .catch(() => {
        dispatch(
          updateAlert({
            show: true,
            type: 'warning',
            dismissible: true,
            text: 'iClicker Course Unlinking failed',
          }),
        );
      })
      .finally(() => unlinkReefDialog.close());
  };

  const renderUnlinkReefDialog = () => {
    return (
      <UnlinkReefDialog
        isCourseLinked={hasInstructorLinkedCourse}
        onClose={unlinkReefDialog.close}
        onSubmit={handleUnlinkReef}
        show={showUnlinkReefDialog}
        disableEnforceFocus={disableEnforceFocus}
      />
    );
  };

  const renderExportDialog = () => (
    <ExportDialog
      courseShortName={courseShortName}
      disableEnforceFocus={disableEnforceFocus}
      courseId={courseId}
    />
  );

  const renderIndividualStudentGradebook = () => {
    return (
      <IndividualStudentGradebook
        hasStudents={gradebookGridData.hasStudents}
        settings={rdxGradebookSettings}
        user={user}
        courseId={courseId}
        getLaunchUrl={getLaunchUrl}
        handleLocalLaunch={handleLocalLaunch}
        getStudentReportLink={getStudentReportLink}
        hasStudentReport={hasStudentReport}
      />
    );
  };

  return (
    <React.Fragment>
      {!isGradebookFetching && isSettingsModalMounted && renderSettingsModal()}
      {showGradeOverrideDialog && renderGradeOverrideDialog()}
      {showLinkReefDialog && renderLinkReefDialog()}
      {showUnlinkReefDialog && renderUnlinkReefDialog()}
      {exportDialog.isShowing && renderExportDialog()}
      {showIndividualStudentGradebook && renderIndividualStudentGradebook()}
      <InstructorGradebook
        courseId={courseId}
        lmsId={lmsId}
        isSectionManager={isSectionManager}
        isGatewayEnabled={isGatewayEnabled}
        disableManualAssignmentsMenu={hasInstructorRestrictedCourseLevelRole}
        loading={loading}
        user={user}
        instructor={instructor}
        enrolledLength={enrolledLength}
        rdxGradebookSettings={rdxGradebookSettings}
        showPercent={showPercent}
        isGradebookFetching={isGradebookFetching}
        isGradebookReFetching={isGradebookReFetching}
        showFilterOptions={showFilterOptions}
        pageCount={pageCount}
        getNextGradebookPage={getNextGradebookPage}
        dispatch={dispatch} // TODO: Would like to move any children using this prop into actions and pass down those actions
        searchPhrase={searchPhrase}
        searchType={searchType}
        exceptionGroups={exceptionGroups}
        filterTypes={filterTypes}
        searchDateFrom={searchDateFrom}
        searchDateTo={searchDateTo}
        gradebookGridData={gradebookGridData}
        updateAssignmentAverages={updateAssignmentAverages}
        collapsedCategoryIds={collapsedCategoryIds}
        getLaunchUrl={getLaunchUrl}
        handleLocalLaunch={handleLocalLaunch}
        onTabChange={onTabChange}
        hasStudentReport={hasStudentReport}
        getStudentReportLink={getStudentReportLink}
        showGradeOverrideDialog={(obj) => handleShowGradeOverrideDialog(obj)}
        permissions={permissions}
        hasInstructorLinkedCourse={hasInstructorLinkedCourse}
        // Do all this stuff in the next level down (and remove from props) - putting it here for visibility
        alert={alert}
        anchorEl={anchorEl}
        exportMenuOpen={exportMenuOpen}
        toggleGradebookExportDialog={exportDialog.toggle}
        handleAlertReef={handleAlertReef}
        reefMenuOpen={reefMenuOpen}
        handleCloseReefMenu={handleCloseReefMenu}
        toggleLinkReefDialog={linkReefDialog.toggle}
        toggleUnlinkReefDialog={unlinkReefDialog.toggle}
        handleReefMenuClick={handleReefMenuClick}
        showSettings={handleShowSettings}
        disableEnforceFocus={disableEnforceFocus}
        refreshGradebookOnSidePanelUpdate={refreshGradebookOnSidePanelUpdate}
        sortColumn={sortColumn}
        sortDirection={sortDirection}
        averages={averages}
        page={page}
        limit={limit}
        count={count}
        getGradebook={getGradebook}
        getGradebookInstructor={getGradebookInstructor}
        isGradebookV2Active={isGradebookV2Active}
        onToggleGradebookV2={handleToggleGradebookV2}
      />
    </React.Fragment>
  );
}

function mapStateToProps(state) {
  // TODO: RNI-3130 Wrap-up This should probably be done in a reducer (same for student-side)
  const requiredIClickerProperties = [
    'isReefLinked',
    'isReefStatusFetching',
    'reefProfile',
    'reefCourseName',
    'reefCourseId',
  ];

  const userIClickerProperties = _pick(
    _get(state, 'user', {}),
    requiredIClickerProperties,
  );
  const getGridData =
    state.gradebook.students.length > 0
      ? getGradebookGridData
      : getEmptyGradebookGridData;

  return {
    loading: state.gradebook.loading,
    user: {
      ...state.gradebook.user,
      ...userIClickerProperties,
    },
    instructor: state.gradebook.instructor,
    showPercent: selectShowPercent(state),
    searchPhrase: state.gradebook.searchPhrase,
    searchType: state.gradebook.searchType,
    searchDateFrom: searchDateFromSelector(state),
    searchDateTo: searchDateToSelector(state),
    pageCount: _get(state, 'gradebook.pages', 0),
    page: state.gradebook.page || 1,
    limit: state.gradebook.limit || 25,
    count: state.gradebook.count || 0,
    showSettings: state.gradebook.showSettings,
    gradebookGridData: getGridData(state),
    collapsedCategoryIds: state.gradebook.collapsedCategoryIds,
    averages: state.gradebook.averages,
    isGradebookFetching: state.gradebook.isGradebookFetching,
    isGradebookReFetching: state.gradebook.isGradebookReFetching,
    isSettingsModalMounted: state.gradebook.isSettingsModalMounted,
    showGradeOverrideDialog: state.gradebook.showGradeOverrideDialog,
    showIndividualStudentGradebook:
      state.gradebook.individualStudentGradebookData?.currentStudent,
    anchorEl: state.gradebook.anchorEl,
    reefMenuOpen: state.gradebook.reefMenuOpen,
    gradeOverrideData: state.gradebook.gradeOverrideData,
    showFilterOptions: state.gradebook.showFilterOptions,
    exceptionGroups: state.gradebook.exceptionGroups,
    filterTypes: state.gradebook.filterTypes,
    alert: state.gradebook.alert,
    exportMenuOpen: state.gradebook.open,
    permissions: state.gradebook.permissions,
    hasInstructorLinkedCourse: state.gradebook.hasInstructorLinkedCourse,
    rdxGradebookSettings: _get(state, 'gradebook.settings', []),
    sortColumn: state.gradebook.sortColumn,
    sortDirection: state.gradebook.sortDirection,
    selectedStudent: _get(state, 'gradebook.selectedStudent', null),
    hasInstructorRestrictedCourseLevelRole: _get(
      state,
      'gradebook.user.has_instructor_restricted_course_level_role',
      false,
    ),
    lmsId: state.gradebook.lmsId,
    isGradebookV2Active: state.gradebook.isGradebookV2Active,
  };
}

// TODO: move stuff here into actions files and then use object notation here
const mapDispatchToProps = (dispatch) => {
  return {
    getGradebook: (courseId, options) =>
      dispatch(getGradebook(courseId, options)),
    getGradebookInstructor: (courseId, options) =>
      dispatch(getGradebookInstructor(courseId, options)),
    updateAssignmentAverages: (assignmentIds) =>
      dispatch(updateAssignmentAverages(assignmentIds)),
    getIndividualStudentData: (courseId, studentId, showLoader) =>
      dispatch(getIndividualStudentData(courseId, studentId, showLoader)),
    isUserLinked: (courseId) => dispatch(isUserLinked(courseId)),
    handleAlertReef: (alertInfo) => dispatch(handleAlertReef(alertInfo)),
    setGradebookV2Active: (isActive) =>
      dispatch(setGradebookV2Active(isActive)),
    dispatch,
  };
};

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(InstructorGradebookContainer);
